export type ProductEventName =
  | "page_view"
  | "search"
  | "search_result_click"
  | "search_no_results"
  | "view_topic"
  | "sign_up"
  | "login"
  | "ai_panel_open"
  | "agent_prompt_pick"
  | "ai_task_start"
  | "ai_task_success"
  | "ai_task_failure"
  | "ai_quota_exhausted"
  | "subscribe_keyword"
  | "unsubscribe_keyword";

const COMMON_PARAMS = [
  "ui_surface",
  "auth_state",
  "content_type",
  "feature_name",
  "result_status",
  "entry_source",
  "page_path",
] as const;

const EVENT_PARAMS: Record<ProductEventName, readonly string[]> = {
  page_view: ["page_title", "page_location", ...COMMON_PARAMS],
  search: ["search_term", "search_source", ...COMMON_PARAMS],
  search_result_click: ["search_term", "topic_name", "result_position", "result_count", "exact_match", ...COMMON_PARAMS],
  search_no_results: ["search_term", ...COMMON_PARAMS],
  view_topic: ["topic_rank", "list_tab", ...COMMON_PARAMS],
  sign_up: ["method", ...COMMON_PARAMS],
  login: ["method", ...COMMON_PARAMS],
  ai_panel_open: COMMON_PARAMS,
  agent_prompt_pick: ["result_position", ...COMMON_PARAMS],
  ai_task_start: COMMON_PARAMS,
  ai_task_success: ["remaining_bucket", ...COMMON_PARAMS],
  ai_task_failure: ["error_type", ...COMMON_PARAMS],
  ai_quota_exhausted: COMMON_PARAMS,
  subscribe_keyword: ["entity_type", ...COMMON_PARAMS],
  unsubscribe_keyword: ["entity_type", ...COMMON_PARAMS],
};

const STRING_LIMITS: Partial<Record<string, number>> = {
  page_location: 1000,
  page_title: 300,
};

function safeScalar(key: string, value: unknown): string | number | boolean | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.slice(0, STRING_LIMITS[key] ?? 100);
  return undefined;
}

export function sanitizeAnalyticsParams(
  name: ProductEventName,
  params: Record<string, unknown> = {},
): Record<string, string | number | boolean> {
  const allowed = new Set(EVENT_PARAMS[name]);
  const output: Record<string, string | number | boolean> = {};
  for (const [key, raw] of Object.entries(params)) {
    if (!allowed.has(key)) continue;
    const value = safeScalar(key, raw);
    if (value !== undefined) output[key] = value;
  }
  return output;
}

export function trackProductEvent(name: ProductEventName, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") return;
  const safe = sanitizeAnalyticsParams(name, params);
  try {
    window.gtag?.("event", name, safe);
    window.clarity?.("event", name);
  } catch {
    // Analytics must never block product behavior.
  }
}

export function setAnalyticsUserId(id: number | string | null): void {
  if (typeof window === "undefined") return;
  try {
    window.gtag?.("set", { user_id: id === null ? null : `member_${String(id)}` });
  } catch {
    // Best-effort analytics identity.
  }
}
